/**
 * Thin wrapper around `fetch` for talking to the FLIK backend.
 *
 * The base URL is configurable through REACT_APP_API_BASE_URL so the same
 * build can point at local/dev/prod.
 *
 * In development it defaults to the relative `/api` path, which the CRA dev
 * server proxies to the backend (see src/setupProxy.js). That sidesteps
 * cross-origin/CORS issues while still hitting the real endpoint:
 * /api/users/register -> localhost:8080/api/users/register.
 *
 * In a static build there is no proxy, so REACT_APP_API_BASE_URL must be an
 * absolute URL to the backend (including its `/api` context-path), e.g.
 * https://dev.api.flik.es/api — which then requires CORS on the backend.
 */
import { getToken, setSession, clearSession, isRemembered } from '../auth/authStore';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

/**
 * Endpoints where a 401 is a genuine authentication failure (wrong password,
 * invalid token) rather than an expired session. We never try to
 * refresh-and-retry these — that would loop or mask the real error.
 */
const AUTH_PATHS = [
  '/users/login',
  '/users/register',
  '/users/google',
  '/users/meta',
  '/users/refresh',
  '/users/logout',
  '/users/activate',
];

function isAuthPath(path) {
  return AUTH_PATHS.some((p) => path.startsWith(p));
}

/**
 * Error thrown for any non-2xx response. Carries the parsed backend payload so
 * callers can surface field-level validation errors and duplicate messages
 * (the backend answers with RFC-7807 ProblemDetail bodies).
 */
export class ApiError extends Error {
  constructor(message, { status, detail, fieldErrors } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.fieldErrors = fieldErrors || {};
  }
}

/**
 * Maps the backend's English error messages to the Spanish UI copy. Falls back
 * to the raw detail for anything not explicitly translated.
 */
function translate(detail) {
  if (!detail) return 'Ha ocurrido un error inesperado.';
  if (/already taken/i.test(detail))
    return 'Ese nombre de usuario ya está en uso.';
  if (/already registered/i.test(detail))
    return 'Ese correo ya está registrado.';
  if (/code is not valid/i.test(detail)) return 'El código no es válido.';
  if (/code has expired/i.test(detail))
    return 'El código ha caducado. Pide uno nuevo.';
  if (/No active code/i.test(detail))
    return 'No hay ningún código activo. Pide uno nuevo.';
  if (/current password|contraseña actual/i.test(detail))
    return 'La contraseña actual no es correcta.';
  if (/bad credentials|invalid credentials|authentication failed/i.test(detail))
    return 'Correo o contraseña incorrectos.';
  if (/not activated|not enabled|account is disabled|user is disabled/i.test(detail))
    return 'Tu cuenta aún no está activada. Revisa tu correo.';
  if (/meta|facebook/i.test(detail))
    return 'No hemos podido validar tu cuenta de Meta. Inténtalo de nuevo.';
  if (/invalid.*(id ?)?token|token.*(invalid|expired)|google/i.test(detail))
    return 'No hemos podido validar tu cuenta de Google. Inténtalo de nuevo.';
  return detail;
}

/**
 * Single network round-trip. Attaches the bearer token and always sends cookies
 * (`credentials: 'include'`) so the httpOnly refresh cookie travels with the
 * request. Returns the raw Response; throws ApiError only on a network failure.
 */
async function performFetch(path, { method = 'GET', body } = {}) {
  // Protected endpoints (everything under /users/me) expect a bearer token.
  // Public endpoints (register/login/activate) simply ignore it, so it is safe
  // to attach whenever a session token is present.
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    throw new ApiError(
      'No hemos podido conectar con el servidor. Inténtalo de nuevo.',
      { status: 0 }
    );
  }
}

// De-duplicates concurrent refresh attempts: if several requests 401 at once,
// they all await the same single POST /users/refresh.
let refreshInFlight = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token and stores it.
 * Returns true on success. On an explicit rejection (the refresh token is
 * invalid/expired) the session is cleared; on a network error the session is
 * left intact so the caller can surface a connection error instead.
 */
function refreshSession() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) {
        clearSession();
        return false;
      }
      const data = await res.json().catch(() => null);
      const token = data && (data.accessToken || data.token || data.jwt);
      if (!token) {
        clearSession();
        return false;
      }
      setSession(token, { remember: isRemembered() });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Called on startup (by the route guard) to recover a session when there is no
 * access token in storage but the httpOnly refresh cookie might still be valid —
 * e.g. the access token was cleared but "Mantener sesión" was on. Returns true if
 * a session is (now) available.
 *
 * Only attempts a refresh when the user asked to be remembered: a session that
 * wasn't meant to persist should not be silently restored.
 */
export function tryRestoreSession() {
  if (getToken()) return Promise.resolve(true);
  if (!isRemembered()) return Promise.resolve(false);
  return refreshSession();
}

async function request(path, options = {}) {
  let response = await performFetch(path, options);

  // A 401 on a protected endpoint means the access token expired. Try once to
  // renew it with the refresh cookie, then replay the original request.
  if (response.status === 401 && !isAuthPath(path) && getToken()) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await performFetch(path, options);
    }
  }

  // Spring answers errors with RFC-7807 bodies whose content-type is
  // `application/problem+json`, so match any JSON-ish content type.
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    throw new ApiError(translate(payload?.detail), {
      status: response.status,
      detail: translate(payload?.detail),
      fieldErrors: payload?.errors,
    });
  }

  return payload;
}

/**
 * POST /users/logout — revokes the refresh token server-side and clears the
 * cookie, then wipes the local session. Best-effort: the local session is
 * cleared even if the network call fails.
 */
export async function logout() {
  try {
    await fetch(`${API_BASE_URL}/users/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
  } catch {
    /* best-effort — clear locally regardless */
  }
  clearSession();
}

/** POST /users/register — creates the account and emails an activation OTP. */
export function registerUser(data) {
  return request('/users/register', { method: 'POST', body: data });
}

/**
 * POST /users/login — signs in with an email-or-username + password and
 * returns the session payload (which carries the auth token). The token field
 * name is read defensively by the caller since it may be `token`/`accessToken`/`jwt`.
 */
export function loginUser({ usernameOrEmail, password, remember = false }) {
  return request('/users/login', {
    method: 'POST',
    // The backend expects the email-or-username under `identifier`. `remember`
    // controls whether the refresh cookie is persistent or session-scoped.
    body: { identifier: usernameOrEmail, password, remember },
  });
}

/**
 * POST /users/google — signs in with Google. The frontend obtains a Google
 * *ID token* via Google Identity Services and posts it here; the backend
 * verifies it (GoogleIdTokenVerifier, audience = app's client id) and answers
 * with the same session payload as a normal login (so the token is read the
 * same defensive way). The DTO field name is centralised here — if the backend
 * expects `credential`/`token` instead of `idToken`, change it in one place.
 */
export function loginWithGoogle({ idToken }) {
  return request('/users/google', {
    method: 'POST',
    body: { idToken },
  });
}

/**
 * POST /users/meta — signs in with Meta (Facebook Login). The frontend obtains
 * a Meta *user access token* via the Facebook JS SDK and posts it here; the
 * backend verifies it against Meta's Graph API and answers with the same
 * session payload as a normal login (token read the same defensive way). The
 * DTO field name is centralised here — if the backend expects `token`/`code`
 * instead of `accessToken`, change it in one place. Same for the path if the
 * controller maps `/users/facebook`.
 */
export function loginWithMeta({ accessToken }) {
  return request('/users/meta', {
    method: 'POST',
    body: { accessToken },
  });
}

/** POST /users/activate — confirms the account with the emailed code. */
export function activateAccount({ email, code }) {
  return request('/users/activate', {
    method: 'POST',
    body: { email, code },
  });
}

/** POST /users/activate/resend — issues and emails a fresh OTP. */
export function resendActivationCode({ email }) {
  return request('/users/activate/resend', {
    method: 'POST',
    body: { email },
  });
}

/* ------------------------------------------------------------------------- *
 *  Activities ("/activities") — the routes/activities shown on the map.
 *  Reads are open to any authenticated user.
 * ------------------------------------------------------------------------- */

/**
 * GET /activities — lists activities, optionally filtered by activity `type`,
 * `name` and a geographic area. The area is a quadrilateral described by exactly
 * four corner points, sent as parallel repeated `lat` and `lng` params
 * (corner i = lat[i]/lng[i]); only activities inside it are returned. Pass the
 * corners as `bounds` (an array of four [lat, lng] pairs). Answers an array of
 * ActivityResponse ({ id, name, activityType, values, createdBy, ... }); the
 * coordinates live in `values.latitude` / `values.longitude`.
 */
export function searchActivities({ type, name, bounds } = {}) {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (name) params.set('name', name);
  if (Array.isArray(bounds) && bounds.length === 4) {
    bounds.forEach(([lat, lng]) => {
      params.append('lat', String(lat));
      params.append('lng', String(lng));
    });
  }
  const qs = params.toString();
  return request(`/activities${qs ? `?${qs}` : ''}`);
}

/**
 * GET /activities/{id} — a single activity by id. Answers one ActivityResponse
 * ({ id, name, activityType, values, createdBy, ... }). Used by the activity
 * detail page so it works on a direct link / refresh, not only via the list.
 */
export function getActivity(id) {
  return request(`/activities/${encodeURIComponent(id)}`);
}

/**
 * GET /activity-types — the activity-type catalogue (read-only) for any
 * authenticated user. Each type is { id, name, slug, fields: [{ key, label,
 * type, placeholder, required, editableByAdmin }], ... }. Used to build the
 * "add activity" form. Answers an array (or a Spring `Page`; the caller unwraps
 * `content` defensively).
 */
export function getActivityTypes() {
  return request('/activity-types');
}

/**
 * POST /activities — creates an activity. ADMIN or EDITOR only. `activityType`
 * is the slug of an existing type and `values` holds the dynamic field values
 * keyed by each field's `key` (validated server-side against the type).
 */
export function createActivity({ name, activityType, values } = {}) {
  return request('/activities', {
    method: 'POST',
    body: { name, activityType, values: values || {} },
  });
}

/**
 * PUT /activities/{id} — updates an existing activity. ADMIN or EDITOR only.
 * Sends the same body shape as createActivity ({ name, activityType, values });
 * answers the updated ActivityResponse.
 */
export function updateActivity(id, { name, activityType, values } = {}) {
  return request(`/activities/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: { name, activityType, values: values || {} },
  });
}

/** DELETE /activities/{id} — removes an activity. ADMIN or EDITOR only (204). */
export function deleteActivity(id) {
  return request(`/activities/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * POST /activities/{id}/images — uploads one or more images (multipart, repeated
 * `files` field). Each is re-encoded to JPEG server-side. ADMIN, or the EDITOR
 * who created it. Answers 200 with the updated ActivityResponse (new imageIds).
 */
export function uploadActivityImages(id, files) {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  return postForm(`/activities/${encodeURIComponent(id)}/images`, form);
}

/**
 * DELETE /activities/{id}/images/{imageId} — removes one image. ADMIN, or the
 * EDITOR who created it. Answers 200 with the updated ActivityResponse.
 */
export function deleteActivityImage(id, imageId) {
  return request(
    `/activities/${encodeURIComponent(id)}/images/${encodeURIComponent(imageId)}`,
    { method: 'DELETE' }
  );
}

/**
 * PUT /activities/{id}/images/{imageId}/variants — sets the three required
 * display-variant crops (twoThirds ≈2:3, square 1:1, half ≈1:2) for one image.
 * Each variant is a CropRect { x, y, width, height } in pixels of the stored
 * image. ADMIN, or the EDITOR who created it. Answers 200 with the updated
 * ActivityResponse (imageCrops now carries this image's variants).
 */
export function setImageVariants(id, imageId, variants) {
  return request(
    `/activities/${encodeURIComponent(id)}/images/${encodeURIComponent(imageId)}/variants`,
    { method: 'PUT', body: variants }
  );
}

/**
 * GET /images/activity/{imageId} — public URL of an activity image, built from
 * an id in the activity's `imageIds`. Used as an <img src>.
 */
export function activityImageUrl(imageId) {
  return imageId == null
    ? null
    : `${API_BASE_URL}/images/activity/${encodeURIComponent(imageId)}`;
}

/**
 * GET /gpx/activity-gpx/{gpxFileId} — raw .gpx file URL, built from an id in
 * the activity's `gpxFileIds`.
 */
export function activityGpxUrl(gpxFileId) {
  return gpxFileId == null
    ? null
    : `${API_BASE_URL}/gpx/activity-gpx/${encodeURIComponent(gpxFileId)}`;
}

/**
 * POST /activities/{id}/gpx — uploads one or more .gpx track files (multipart,
 * repeated `files` field). Only allowed for senderismo/ciclismo activities.
 * ADMIN, or the EDITOR who created it. Answers 200 with the updated
 * ActivityResponse (new gpxFileIds).
 */
export function uploadActivityGpx(id, files) {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  return postForm(`/activities/${encodeURIComponent(id)}/gpx`, form);
}

/**
 * POST /activities/bulk/via-ferrata — asynchronous bulk import of vía ferrata
 * activities from a JSON file, sent as multipart/form-data under the `file`
 * field. The backend processes the whole file in the background and emails the
 * result when done, so this only kicks off the job (typically 202). Uses fetch
 * directly rather than `request` because that helper JSON-stringifies the body
 * and forces a JSON Content-Type — multipart needs the browser to set its own
 * boundary. Bearer token attached (ADMIN only); errors surface as ApiError.
 */
export function bulkImportViaFerrata(file) {
  return postMultipart('/activities/bulk/via-ferrata', file);
}

/**
 * POST <path> with a single `file` field as multipart/form-data. Uses fetch
 * directly rather than `request` because that helper JSON-stringifies the body
 * and forces a JSON Content-Type — multipart needs the browser to set its own
 * boundary. Bearer token attached; errors surface as ApiError.
 */
async function postMultipart(path, file, fields) {
  const form = new FormData();
  form.append('file', file);
  if (fields) {
    for (const [key, value] of Object.entries(fields)) {
      if (value != null) form.append(key, value);
    }
  }
  return postForm(path, form);
}

/**
 * POSTs a prepared FormData with the same bearer-token + refresh-and-retry
 * handling as `request`. Lets the browser set the multipart boundary itself.
 */
async function postForm(path, form) {
  // Bearer token per attempt (a refresh below rotates it, so re-read it).
  const send = async () => {
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      return await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: form,
      });
    } catch {
      throw new ApiError(
        'No hemos podido conectar con el servidor. Inténtalo de nuevo.',
        { status: 0 }
      );
    }
  };

  let response = await send();
  // Mirror request(): a 401 means the access token expired — refresh once with
  // the cookie and replay. FormData is reusable, so we just re-send it.
  if (response.status === 401 && !isAuthPath(path) && getToken()) {
    if (await refreshSession()) response = await send();
  }

  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('json')
    ? await response.json().catch(() => null)
    : null;
  if (!response.ok) {
    throw new ApiError(translate(payload?.detail), {
      status: response.status,
      detail: translate(payload?.detail),
      fieldErrors: payload?.errors,
    });
  }
  return payload;
}

/* ------------------------------------------------------------------------- *
 *  Profile ("/users/me") — the signed-in user's own account.
 *  All of these require a valid session token (attached automatically above).
 * ------------------------------------------------------------------------- */

/**
 * GET /users/me — the signed-in user's profile (UserResponse). Companion to the
 * PUT below; used to populate the profile page on load.
 */
export function getMyProfile() {
  return request('/users/me');
}

/**
 * PUT /users/me — partial profile update. Every field is optional; only the
 * ones provided are changed. Accepts { name, surname, birthday, genre,
 * biography } and answers 200 with the updated UserResponse.
 */
export function updateProfile(data) {
  return request('/users/me', { method: 'PUT', body: data });
}

/**
 * POST /users/me/avatar — uploads the profile picture as multipart/form-data
 * under the `file` field. The backend stores the whole image and only records
 * the crop, so `file` is the full picture and `crop` ({ x, y, width, height }
 * in source-image pixels) is sent alongside as form fields. Answers 200 with
 * the updated UserResponse.
 */
export function uploadAvatar(file, crop) {
  const fields = crop
    ? { cropX: crop.x, cropY: crop.y, cropWidth: crop.width, cropHeight: crop.height }
    : null;
  return postMultipart('/users/me/avatar', file, fields);
}

/**
 * PUT /users/me/avatar/crop — re-crop the existing avatar without re-uploading.
 * Body { x, y, width, height } in source-image pixels. 400 if there is no
 * avatar. Answers 200 with the updated UserResponse.
 */
export function updateAvatarCrop(crop) {
  return request('/users/me/avatar/crop', { method: 'PUT', body: crop });
}

/**
 * DELETE /users/me/avatar — removes the current profile picture. Answers 200
 * with the updated UserResponse (now without a profileImageId).
 */
export function deleteAvatar() {
  return request('/users/me/avatar', { method: 'DELETE' });
}

/**
 * GET /images/avatar/{id} — public URL of an avatar image, built from a
 * profile's `profileImageId`. Returns null when there is no image so callers
 * can fall back to the initials/icon placeholder. Used as an <img src>.
 */
export function avatarUrl(id) {
  return id == null ? null : `${API_BASE_URL}/images/avatar/${encodeURIComponent(id)}`;
}

/** GET /users/me/preferences — { publicProfile, showRoutes, notificationsEnabled }. */
export function getPreferences() {
  return request('/users/me/preferences');
}

/**
 * PUT /users/me/preferences — partial preferences update (each flag is a
 * nullable Boolean; send only what changes). Answers 200 with the full
 * PreferencesResponse.
 */
export function updatePreferences(data) {
  return request('/users/me/preferences', { method: 'PUT', body: data });
}

/**
 * POST /users/me/password/change — begins a password change. Validates the
 * current password and emails a confirmation code. Answers 202 (no body);
 * finish with confirmPasswordChange.
 */
export function changePassword({ currentPassword, newPassword }) {
  return request('/users/me/password/change', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

/** POST /users/me/password/confirm — confirms the change with the emailed code (204). */
export function confirmPasswordChange({ code }) {
  return request('/users/me/password/confirm', {
    method: 'POST',
    body: { code },
  });
}

/**
 * POST /users/me/delete — begins account deletion. Emails a confirmation code.
 * Answers 202 (no body); finish with confirmAccountDeletion.
 */
export function requestAccountDeletion() {
  return request('/users/me/delete', { method: 'POST' });
}

/** POST /users/me/delete/confirm — confirms deletion with the emailed code (204). */
export function confirmAccountDeletion({ code }) {
  return request('/users/me/delete/confirm', {
    method: 'POST',
    body: { code },
  });
}

/* ------------------------------------------------------------------------- *
 *  Admin ("/admin/users") — administration dashboard. All require an ADMIN
 *  session token (attached automatically above).
 * ------------------------------------------------------------------------- */

/**
 * GET /admin/users — paginated user list. Optional `search` (matches username,
 * name and email) and `role` (user type) filters; `page`/`size` drive Spring
 * pagination. Answers a Spring `Page` ({ content, totalElements, totalPages,
 * number, size, ... }).
 */
export function getAdminUsers({ search, role, page = 0, size = 10 } = {}) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (role) params.set('role', role);
  params.set('page', String(page));
  params.set('size', String(size));
  return request(`/admin/users?${params.toString()}`);
}

/** GET /admin/users/count — total number of registered users. */
export function getAdminUsersCount() {
  return request('/admin/users/count');
}

/** GET /admin/users/premium/count — number of premium users. */
export function getAdminPremiumCount() {
  return request('/admin/users/premium/count');
}

/**
 * GET /admin/users/registrations — registrations for a given `year`, restricted
 * to the requested `months` (an array of 1-based month numbers, sent as repeated
 * `month` params).
 */
export function getAdminRegistrations({ year, months } = {}) {
  const params = new URLSearchParams();
  if (year) params.set('year', String(year));
  (months || []).forEach((m) => params.append('month', String(m)));
  return request(`/admin/users/registrations?${params.toString()}`);
}

/* ------------------------------------------------------------------------- *
 *  Admin ("/admin/activity-types") — activity-type catalogue. Each type has a
 *  set of default fields (always preserved by the backend) plus optional
 *  admin-defined custom fields. All require an ADMIN session token.
 * ------------------------------------------------------------------------- */

/**
 * GET /admin/activity-types — the full catalogue of activity types. Answers an
 * array (or a Spring `Page`; the caller unwraps `content` defensively).
 */
export function getAdminActivityTypes() {
  return request('/admin/activity-types');
}

/** GET /admin/activity-types/{id} — a single activity type with all its fields. */
export function getAdminActivityType(id) {
  return request(`/admin/activity-types/${encodeURIComponent(id)}`);
}

/**
 * POST /admin/activity-types — creates an activity type (CreateActivityTypeRequest
 * { name, customFields? }). The backend adds its default fields automatically;
 * `customFields` is optional and holds the admin-defined extras (each a
 * FieldDefinitionRequest { key, label, type, placeholder?, required }).
 */
export function createAdminActivityType({ name, customFields } = {}) {
  return request('/admin/activity-types', {
    method: 'POST',
    body: { name, customFields: customFields || [] },
  });
}

/**
 * PUT /admin/activity-types/{id}/fields — replaces the custom fields in one
 * call (UpdateFieldsRequest { customFields }). The default fields are always
 * preserved by the backend; only the admin-editable set is sent.
 */
export function replaceAdminActivityTypeFields(id, customFields) {
  return request(`/admin/activity-types/${encodeURIComponent(id)}/fields`, {
    method: 'PUT',
    body: { customFields: customFields || [] },
  });
}
